import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { SettingsPage } from "./pages/settings";
import { FunnelCreatePage } from "./pages/funnel-create";
import { FunnelEditPage } from "./pages/funnel-edit";
import { WazzupSettingsPage } from "./pages/wazzup-settings";
import { AuthSuccessPage } from "./pages/auth-success";
import { LoginPage } from "./pages/login";
import { ConnectAmoPage } from "./pages/connect-amo";
import { useAuthStore } from "./stores/auth-store";
import { api } from "./lib/api";
import { ToastProvider } from "./components/ui/toast";
import { AppHeader } from "./components/app-header";
import "./index.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

function AppLayout() {
  return (
    <div className="flex h-screen flex-col">
      <AppHeader />
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}

function AppShell() {
  const { isReady, token, userJwt, init, setAccountToken, clearUserJwt } = useAuthStore();

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    init(params);
  }, [init]);

  const me = useQuery({
    queryKey: ["auth", "me", userJwt],
    queryFn: () => api.getMe(userJwt!),
    enabled: isReady && Boolean(userJwt) && !token,
    retry: false,
  });

  const accountToken = useQuery({
    queryKey: ["auth", "account-token", userJwt],
    queryFn: () => api.getAccountToken(userJwt!),
    enabled:
      isReady &&
      Boolean(userJwt) &&
      Boolean(me.data?.hasAmoAccount) &&
      !token,
    retry: false,
  });

  React.useEffect(() => {
    if (accountToken.data?.token) {
      setAccountToken(accountToken.data.token);
    }
  }, [accountToken.data?.token, setAccountToken]);

  React.useEffect(() => {
    if (me.error) {
      clearUserJwt();
    }
  }, [clearUserJwt, me.error]);

  if (!isReady) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Загрузка...
      </div>
    );
  }

  if (!userJwt && token) {
    return (
      <Routes>
        <Route path="/auth/success" element={<AuthSuccessPage />} />
        <Route element={<AppLayout />}>
          <Route path="/" element={<Navigate to="/settings" replace />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/wazzup" element={<WazzupSettingsPage />} />
          <Route path="/funnels/new" element={<FunnelCreatePage />} />
          <Route path="/funnels/:id" element={<FunnelEditPage />} />
          <Route path="*" element={<Navigate to="/settings" replace />} />
        </Route>
      </Routes>
    );
  }

  if (userJwt && me.isLoading) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Проверяем аккаунт...
      </div>
    );
  }

  if (userJwt && me.data?.hasAmoAccount && !token) {
    if (accountToken.isError) {
      return <Navigate to="/connect-amo" replace />;
    }

    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Загружаем интерфейс...
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/auth/success" element={<AuthSuccessPage />} />
      <Route
        path="/login"
        element={userJwt ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route
        path="/connect-amo"
        element={
          userJwt ? <ConnectAmoPage /> : <Navigate to="/login" replace />
        }
      />

      {!userJwt ? (
        <Route path="*" element={<Navigate to="/login" replace />} />
      ) : !me.data?.hasAmoAccount ? (
        <Route path="*" element={<Navigate to="/connect-amo" replace />} />
      ) : token ? (
        <Route element={<AppLayout />}>
          <Route path="/" element={<Navigate to="/settings" replace />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/wazzup" element={<WazzupSettingsPage />} />
          <Route path="/funnels/new" element={<FunnelCreatePage />} />
          <Route path="/funnels/:id" element={<FunnelEditPage />} />
          <Route path="*" element={<Navigate to="/settings" replace />} />
        </Route>
      ) : (
        <Route path="*" element={<Navigate to="/connect-amo" replace />} />
      )}
    </Routes>
  );
}

const root = document.getElementById("root");
if (!root) throw new Error("Root element not found");

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <AppShell />
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
